"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Download,
  ImageIcon,
  Loader2,
  Video,
  Play,
  ChevronDown,
  ChevronUp,
  Sparkles,
  X,
  Pencil,
} from "lucide-react";
import {
  GenerateResponse,
  VideoStatusResponse,
  EditStatusResponse,
  getImageUrlWithAuth,
  getVideoUrlWithAuth,
} from "@/lib/api";

interface ImageDisplayProps {
  image: GenerateResponse | null;
  isGenerating: boolean;
  isGeneratingVideo: boolean;
  videoStatus: VideoStatusResponse | null;
  isEditing?: boolean;
  editStatus?: EditStatusResponse | null;
  onGenerateVideo?: (prompt: string) => void;
  onEditImage?: (prompt: string) => void;
  onDownload?: () => void;
  onDownloadVideo?: () => void;
}

export function ImageDisplay({
  image,
  isGenerating,
  isGeneratingVideo,
  videoStatus,
  isEditing = false,
  editStatus,
  onGenerateVideo,
  onEditImage,
  onDownload,
  onDownloadVideo,
}: ImageDisplayProps) {
  const [activeTab, setActiveTab] = useState<"image" | "video">("image");
  const [showVideoPrompt, setShowVideoPrompt] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState("");
  const [showEditPrompt, setShowEditPrompt] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");

  // Reset prompt panels when image changes
  useEffect(() => {
    setShowVideoPrompt(false);
    setVideoPrompt(image?.prompt || "");
    setShowEditPrompt(false);
    setEditPrompt("");
  }, [image?.image_id]);

  // Pre-fill with image prompt when opening panel
  useEffect(() => {
    if (showVideoPrompt && image?.prompt && !videoPrompt) {
      setVideoPrompt(image.prompt);
    }
  }, [showVideoPrompt, image?.prompt]);

  const handleStartGeneration = () => {
    if (videoPrompt.trim()) {
      onGenerateVideo?.(videoPrompt.trim());
      setShowVideoPrompt(false);
    }
  };

  const handleToggleVideoPrompt = () => {
    if (!showVideoPrompt) {
      // Opening: pre-fill with image prompt
      setVideoPrompt(image?.prompt || "");
      setShowEditPrompt(false); // Close edit panel
    }
    setShowVideoPrompt(!showVideoPrompt);
  };

  const handleToggleEditPrompt = () => {
    if (!showEditPrompt) {
      setShowVideoPrompt(false); // Close video panel
    }
    setShowEditPrompt(!showEditPrompt);
  };

  const handleStartEdit = () => {
    if (editPrompt.trim()) {
      onEditImage?.(editPrompt.trim());
      setShowEditPrompt(false);
    }
  };

  // Check if video is available
  const hasVideo =
    videoStatus?.status === "completed" ||
    (image?.video_id && image?.video_status === "completed") || false;
  const videoId = videoStatus?.video_id || image?.video_id;

  // Switch to video tab when video completes
  if (hasVideo && activeTab === "image" && videoStatus?.status === "completed") {
    setActiveTab("video");
  }

  // Show loading when generating and no image selected
  if (isGenerating && !image) {
    return (
      <div className="relative aspect-square w-full max-w-2xl mx-auto bg-secondary/30 rounded-xl flex flex-col items-center justify-center gap-4 border border-border">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground">Creating your image...</p>
      </div>
    );
  }

  // Show placeholder when no image and not generating
  if (!image) {
    return (
      <div className="relative aspect-square w-full max-w-2xl mx-auto bg-secondary/30 rounded-xl flex flex-col items-center justify-center gap-4 border border-border border-dashed">
        <ImageIcon className="h-16 w-16 text-muted-foreground/50" />
        <p className="text-muted-foreground">
          Your generated image will appear here
        </p>
      </div>
    );
  }

  const imageUrl = getImageUrlWithAuth(image.image_id);
  const videoUrl = videoId ? getVideoUrlWithAuth(videoId) : null;

  return (
    <div className="space-y-4">
      {/* Tab Switcher - Only show if video exists */}
      {hasVideo && (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "image" | "video")}
          className="w-full"
        >
          <TabsList className="grid w-full max-w-xs mx-auto grid-cols-2">
            <TabsTrigger value="image" className="gap-2">
              <ImageIcon className="h-4 w-4" />
              Image
            </TabsTrigger>
            <TabsTrigger value="video" className="gap-2">
              <Play className="h-4 w-4" />
              Video
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Content Container */}
      <div className="relative w-full max-w-2xl mx-auto rounded-xl overflow-hidden border border-border bg-secondary/20">
        {/* Image Generation Overlay - shown when generating new image while viewing history */}
        {isGenerating && (
          <div className="absolute top-2 right-2 z-10 flex items-center gap-2 px-3 py-1.5 bg-primary/90 rounded-full shadow-lg">
            <Loader2 className="h-4 w-4 animate-spin text-white" />
            <span className="text-xs font-medium text-white">New image generating...</span>
          </div>
        )}

        {/* Video Generation Overlay */}
        {isGeneratingVideo && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center px-4">
              <p className="text-white font-medium">Generating video...</p>
              <p className="text-white/70 text-sm mt-1">
                This takes approximately 2-3 minutes
              </p>
            </div>
          </div>
        )}

        {/* Image Edit Overlay */}
        {isEditing && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-purple-900/70 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
            <div className="text-center px-4">
              <p className="text-white font-medium">Editing image...</p>
              <p className="text-white/70 text-sm mt-1">
                {editStatus?.progress
                  ? `Progress: ${Math.round(editStatus.progress * 100)}%`
                  : "This takes approximately 2 minutes"}
              </p>
            </div>
          </div>
        )}

        {/* Image/Video Display */}
        {activeTab === "image" || !hasVideo ? (
          <div className="relative aspect-square">
            <Image
              src={imageUrl}
              alt={image.prompt}
              fill
              className="object-contain"
              priority
              unoptimized
            />
          </div>
        ) : (
          <div className="relative aspect-video">
            <video
              src={videoUrl || undefined}
              controls
              autoPlay
              loop
              className="w-full h-full object-contain"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>

      {/* Image/Video Info */}
      <div className="flex flex-wrap gap-2 justify-center">
        <Badge variant="secondary">
          {image.width} x {image.height}
        </Badge>
        <Badge variant="secondary">Seed: {image.seed}</Badge>
        <Badge variant="secondary">{image.generation_time}s</Badge>
        {hasVideo && (
          <Badge variant="default" className="bg-green-600">
            Video Ready
          </Badge>
        )}
        {isGeneratingVideo && (
          <Badge variant="secondary" className="animate-pulse">
            Video Generating...
          </Badge>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 justify-center">
        {activeTab === "image" || !hasVideo ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onDownload}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download Image
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadVideo}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Download Video
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleVideoPrompt}
          disabled={isGeneratingVideo || hasVideo}
          className="gap-2"
        >
          {isGeneratingVideo ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : hasVideo ? (
            <>
              <Video className="h-4 w-4" />
              Video Created
            </>
          ) : (
            <>
              <Video className="h-4 w-4" />
              Generate Video
              {showVideoPrompt ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </>
          )}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleToggleEditPrompt}
          disabled={isEditing || (hasVideo && activeTab === "video")}
          className="gap-2"
        >
          {isEditing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Editing...
            </>
          ) : (
            <>
              <Pencil className="h-4 w-4" />
              Edit Image
              {showEditPrompt ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </>
          )}
        </Button>
      </div>

      {/* Video Prompt Input Panel */}
      {showVideoPrompt && !hasVideo && !isGeneratingVideo && (
        <div className="max-w-2xl mx-auto border border-border rounded-lg p-4 bg-secondary/30 space-y-3 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4 text-primary" />
              Video Prompt
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVideoPrompt(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Textarea
            value={videoPrompt}
            onChange={(e) => setVideoPrompt(e.target.value)}
            placeholder="Describe the motion and style of the video..."
            className="min-h-[80px] resize-none"
          />

          <p className="text-xs text-muted-foreground">
            Tip: Describe movement like &quot;walking slowly&quot;, &quot;camera panning left&quot;, &quot;gentle wind blowing&quot;
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowVideoPrompt(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleStartGeneration}
              disabled={!videoPrompt.trim()}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              Start Generation
            </Button>
          </div>
        </div>
      )}

      {/* Edit Image Prompt Input Panel */}
      {showEditPrompt && !isEditing && (
        <div className="max-w-2xl mx-auto border border-purple-500/30 rounded-lg p-4 bg-purple-500/10 space-y-3 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-400">
              <Pencil className="h-4 w-4" />
              Edit Image
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowEditPrompt(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Textarea
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            placeholder="Describe your edit... (e.g., 'make the background sunset', 'add a hat', 'change hair to blonde')"
            className="min-h-[80px] resize-none border-purple-500/30 focus:border-purple-500"
          />

          <p className="text-xs text-muted-foreground">
            Tip: Describe what you want to change in the image. Be specific about colors, objects, or styles.
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditPrompt(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleStartEdit}
              disabled={!editPrompt.trim()}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <Pencil className="h-4 w-4" />
              Start Editing
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
